/**
 * DiagramWithSideLabels — used by the Autonomous Intelligence & Enterprise
 * Security sections. Central asset graphic flanked by 3 crisp HTML labels on
 * the left and 3 on the right, plus an atmospheric bloom behind the asset.
 *
 * Props:
 *   image       — src of the central diagram asset
 *   leftLabels  — Array<{ icon, title, desc }> for the left column
 *   rightLabels — Array<{ icon, title, desc }> for the right column
 *   bloomColor  — CSS color for the atmospheric radial bloom
 *   imgScale    — object-fit scale factor for the central asset (default 1.5)
 */
export default function DiagramWithSideLabels({ image, leftLabels, rightLabels, bloomColor, imgScale = 1.5 }) {
  return (
    <div className="relative w-full max-w-[760px] mx-auto" aria-hidden>
      <div
        className="absolute inset-[-8%] pointer-events-none"
        style={{
          background: `radial-gradient(closest-side, ${bloomColor}, transparent 70%)`,
          filter: "blur(56px)",
        }}
      />
      <div className="relative grid grid-cols-[1fr_1.2fr_1fr] gap-3 sm:gap-4 lg:gap-5 items-center">
        {/* Left labels */}
        <div className="space-y-7 sm:space-y-8 text-right">
          {leftLabels.map((l, i) => (
            <div key={i} className="flex items-start gap-2.5 justify-end">
              <div>
                <div className="font-display text-platinum text-[13px] sm:text-[14px] tracking-tight leading-tight">{l.title}</div>
                <div className="mt-1 text-[11px] sm:text-[11.5px] text-platinum/55 leading-snug">{l.desc}</div>
              </div>
              <l.icon className="w-4 h-4 text-sapphire-glow mt-0.5 shrink-0" />
            </div>
          ))}
        </div>

        {/* Central asset — cropped + diagram mask so only the central graphic remains
             (top/bottom/side embedded text and labels fade to transparent). */}
        <div className="relative aspect-square slow-float">
          <img
            src={image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover asset-feather-diagram breathe-glow"
            style={{ transform: `scale(${imgScale})`, objectPosition: "center 50%" }}
            draggable="false"
          />
        </div>

        {/* Right labels */}
        <div className="space-y-7 sm:space-y-8">
          {rightLabels.map((l, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <l.icon className="w-4 h-4 text-sapphire-glow mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-platinum text-[13px] sm:text-[14px] tracking-tight leading-tight">{l.title}</div>
                <div className="mt-1 text-[11px] sm:text-[11.5px] text-platinum/55 leading-snug">{l.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
